
import React from 'react';
import { PolicyLayout } from '@/components/policy/PolicyLayout';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';

const PrivacyPolicy = () => {
  return (
    <PolicyLayout 
      title="Privacy Policy" 
      lastUpdated="June 1, 2024"
    >
      <div className="text-sm">
        <div className="flex justify-between items-center mb-6">
          <div>
            <p className="text-muted-foreground">
              This Privacy Policy explains how we collect, use, and protect your personal information.
            </p>
          </div>
          <Link to="/privacy-settings">
            <Button variant="outline" size="sm">Manage Privacy Settings</Button>
          </Link>
        </div>
        
        <h2>1. Introduction</h2>
        <p>At Travilis, we take your privacy seriously. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our website, mobile application, and related services (collectively, the "Services"). By using our Services, you consent to the practices described in this Privacy Policy.</p>
        
        <h2>2. Information We Collect</h2>
        <h3>2.1 Personal Information</h3>
        <p>We may collect personal information that you provide directly to us, including:</p>
        <ul>
          <li>Contact information: Name, email address, phone number</li>
          <li>Account information: Username, password</li>
          <li>Profile information: Photograph, bio, preferences, and any declaration regarding your country of origin</li>
          <li>Payment information</li>
          <li>Communications: Any messages or other communications you send to us</li>
        </ul>
        
        <h3>2.2 Usage Information</h3>
        <p>We automatically collect certain information about your device and your interaction with our Services, including:</p>
        <ul>
          <li>IP address and device identifiers</li>
          <li>Browser type and operating system</li>
          <li>Pages viewed and actions taken</li>
          <li>Dates and times of access</li>
        </ul>

        <h2>3. How We Use Your Information</h2>
        <p>We use the information we collect to:</p>
        <ul>
          <li>Provide, maintain, and improve our Services</li>
          <li>Process transactions and send related information</li>
          <li>Create and manage your account</li>
          <li>Connect Travelers with Hosts</li>
          <li>Send service-related notifications and marketing communications (with your consent where required)</li>
          <li>Respond to your comments, questions, and requests</li>
          <li>Personalize your experience</li>
          <li>Monitor and analyze usage patterns</li>
          <li>Detect, prevent, and address fraud and abuse</li>
          <li>Comply with legal obligations</li>
        </ul>

        <h3>3.1 Legal Basis for Processing</h3>
        <p>Where required by applicable law, our processing of your personal data is based on one or more of the following:</p>
        <ul>
          <li>Your consent</li>
          <li>The necessity to perform a contract with you</li>
          <li>Compliance with legal obligations</li>
          <li>Our legitimate interests, provided such interests do not override your fundamental rights and freedoms</li>
        </ul>

        <h2>4. Cookies and Tracking Technologies</h2>
        <p>We use cookies, web beacons, and similar tracking technologies to:</p>
        <ul>
          <li>Enhance your experience on our Services</li>
          <li>Analyze usage and trends</li>
          <li>Provide personalized content and advertisements</li>
        </ul>
        <p>You can manage your cookie preferences through your browser settings. Please refer to our Cookie Policy for more detailed information.</p>

        <h2>5. Information Sharing</h2>
        <p>We may share your information with:</p>
        <ul>
          <li>Other Users: As necessary for the functioning of our Services</li>
          <li>Service Providers: Third-party vendors who perform services on our behalf (e.g., payment processing, data analysis)</li>
          <li>Business Partners: With your consent or as otherwise permitted by law</li>
          <li>Legal Authorities: When required by law or to protect our rights</li>
        </ul>
        <p>We require that all third parties with whom we share your information adhere to similar data protection standards.</p>

        <h2>6. Data Security</h2>
        <p>We implement industry-standard security measures to protect your personal information. These measures include encryption, access controls, and regular security assessments. However, no method of transmission over the Internet or electronic storage is completely secure, and we cannot guarantee absolute security.</p>

        <h2>7. Data Retention</h2>
        <p>We retain your personal information for as long as necessary to provide our Services and fulfill the purposes described in this Privacy Policy. The specific retention period may vary based on the type of data and applicable legal requirements.</p>

        <h2>8. Your Rights and Choices</h2>
        <p>Depending on your jurisdiction, you may have the right to:</p>
        <ul>
          <li>Access, update, or delete your personal information</li>
          <li>Object to or restrict the processing of your personal data</li>
          <li>Withdraw your consent at any time (where processing is based on consent)</li>
          <li>Opt out of marketing communications</li>
          <li>Manage your cookie preferences</li>
        </ul>
        <p>To exercise these rights, please contact us using the details provided in Section 15.</p>

        <h2>9. International Data Transfers</h2>
        <p>Your information may be transferred to and maintained on servers located outside your state, province, country, or other governmental jurisdiction where data protection laws may differ from those in your jurisdiction. We take steps to ensure that your information is treated securely and in accordance with this Privacy Policy.</p>

        <h2>10. Children's Privacy</h2>
        <p>Our Services are not intended for individuals under the age of 18. We do not knowingly collect personal information from children under 18. If we become aware that we have inadvertently collected personal data from a child under 18, we will take steps to delete the information as soon as possible.</p>

        <h2>11. Data Controller and Data Protection Officer</h2>
        <p>Travilis, Inc. is the data controller for your personal information. For any questions regarding the processing of your personal data or to exercise your rights under applicable data protection laws, please contact our Data Protection Officer (DPO) at dpo@travilis.com.</p>

        <h2>12. Third-Party Links</h2>
        <p>Our Services may contain links to third-party websites or services that are not operated by us. This Privacy Policy does not apply to those third-party websites. We are not responsible for the privacy practices of these other sites, and we encourage you to review their privacy policies before providing any personal information.</p>

        <h2>13. Do Not Track Signals</h2>
        <p>Some browsers offer "Do Not Track" (DNT) signals that you can activate to inform websites that you do not wish to be tracked. At this time, our Services do not respond to DNT signals. Please refer to this Privacy Policy for information on how we collect and use your data.</p>

        <h2>14. Changes to This Privacy Policy</h2>
        <p>We may update this Privacy Policy from time to time. Any changes will be posted on this page with an updated "Last Updated" date. We encourage you to review this Privacy Policy periodically for any updates.</p>

        <h2>15. Contact Us</h2>
        <p>If you have any questions or concerns about this Privacy Policy, or if you wish to exercise your rights, please contact us at:</p>
        <p>Email: privacy@travilis.com</p>
      </div>
    </PolicyLayout>
  );
};

export default PrivacyPolicy;
